/// <reference types="vite/client" />
import { createClient } from '@supabase/supabase-js'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './config'

// Supabase configuration (pulled from env; no hardcoded secrets)
const supabaseUrl = SUPABASE_URL
const supabaseAnonKey = SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Supabase environment variables are missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  )
}

// Debug logging (safe: does not print keys)
console.log('🔧 Supabase Config:', {
  urlDefined: !!supabaseUrl,
  anonKeyDefined: !!supabaseAnonKey,
})

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Database types (will be generated from Supabase later)
export interface Database {
  public: {
    Tables: {
      customers: {
        Row: {
          id: string
          name: string
          phone: string
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          phone: string
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          phone?: string
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      tables: {
        Row: {
          id: string
          number: number
          seats: number
          zone: string
          status: string
          manual_status: string
          last_status_change: string
          status_reason: string | null
          assigned_staff_id: string | null
          is_active: boolean
          created_at: string
          updated_at: string
          floor_plan_x_pct: number | null
          floor_plan_y_pct: number | null
          floor_plan_shape: string | null
          floor_plan_room_id: string | null
        }
        Insert: {
          id?: string
          number: number
          seats: number
          zone?: string
          status?: string
          manual_status?: string
          last_status_change?: string
          status_reason?: string | null
          assigned_staff_id?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
          floor_plan_x_pct?: number | null
          floor_plan_y_pct?: number | null
          floor_plan_shape?: string | null
          floor_plan_room_id?: string | null
        }
        Update: {
          id?: string
          number?: number
          seats?: number
          zone?: string
          status?: string
          manual_status?: string
          last_status_change?: string
          status_reason?: string | null
          assigned_staff_id?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
          floor_plan_x_pct?: number | null
          floor_plan_y_pct?: number | null
          floor_plan_shape?: string | null
          floor_plan_room_id?: string | null
        }
      }
      table_groups: {
        Row: {
          id: string
          tenant_id: string
          anchor_table_id: string
          table_ids: string[]
          room_id: string | null
          label: string | null
          status: string
          created_by: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          tenant_id: string
          anchor_table_id: string
          table_ids?: string[]
          room_id?: string | null
          label?: string | null
          status?: string
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          tenant_id?: string
          anchor_table_id?: string
          table_ids?: string[]
          room_id?: string | null
          label?: string | null
          status?: string
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      table_group_members: {
        Row: {
          group_id: string
          table_id: string
          sort_index: number
          created_at: string
        }
        Insert: {
          group_id: string
          table_id: string
          sort_index?: number
          created_at?: string
        }
        Update: {
          group_id?: string
          table_id?: string
          sort_index?: number
          created_at?: string
        }
      }
      floor_plan_rooms: {
        Row: {
          id: string
          tenant_id: string
          room_id: string
          label: string | null
          grid_width: number
          grid_height: number
          sort_order: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          tenant_id: string
          room_id: string
          label?: string | null
          grid_width?: number
          grid_height?: number
          sort_order?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          tenant_id?: string
          room_id?: string
          label?: string | null
          grid_width?: number
          grid_height?: number
          sort_order?: number
          created_at?: string
          updated_at?: string
        }
      }
      shifts: {
        Row: {
          id: string
          name: string
          start_time: string
          end_time: string
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          start_time: string
          end_time: string
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          start_time?: string
          end_time?: string
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      reservations: {
        Row: {
          id: string
          tenant_id: string
          customer_id: string
          table_id: string | null
          shift_id: string
          date: string
          time: string
          party_size: number
          status: string
          source: string | null
          notes: string | null
          seated_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          tenant_id: string
          customer_id: string
          table_id?: string | null
          shift_id: string
          date: string
          time: string
          party_size: number
          status?: string
          source?: string | null
          notes?: string | null
          seated_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          tenant_id?: string
          customer_id?: string
          table_id?: string | null
          shift_id?: string
          date?: string
          time?: string
          party_size?: number
          status?: string
          source?: string | null
          notes?: string | null
          seated_at?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      reservation_tables: {
        Row: {
          reservation_id: string
          table_id: string
          tenant_id: string
          sort_index: number
          created_at: string
        }
        Insert: {
          reservation_id: string
          table_id: string
          tenant_id: string
          sort_index?: number
          created_at?: string
        }
        Update: {
          reservation_id?: string
          table_id?: string
          tenant_id?: string
          sort_index?: number
          created_at?: string
        }
      }
      waitlist: {
        Row: {
          id: string
          customer_id: string
          date: string
          time: string
          party_size: number
          priority: number
          status: string
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          customer_id: string
          date: string
          time: string
          party_size: number
          priority?: number
          status?: string
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          customer_id?: string
          date?: string
          time?: string
          party_size?: number
          priority?: number
          status?: string
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      sync_queue: {
        Row: {
          id: string
          table_name: string
          record_id: string
          operation: string
          data: any
          device_id: string | null
          created_at: string
          processed_at: string | null
          status: string
        }
        Insert: {
          id?: string
          table_name: string
          record_id: string
          operation: string
          data?: any
          device_id?: string | null
          created_at?: string
          processed_at?: string | null
          status?: string
        }
        Update: {
          id?: string
          table_name?: string
          record_id?: string
          operation?: string
          data?: any
          device_id?: string | null
          created_at?: string
          processed_at?: string | null
          status?: string
        }
      }
    }
  }
}

// Typed Supabase client
export const supabaseTyped = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})
